import type { AgentForm, TurnDetectorConfig } from '../../../utilities';
import {
  ENDPOINTING_MODE,
  MODES_WITH_ENDPOINTING,
  MODES_WITH_INTERRUPTION,
  MODES_WITH_VAD,
  REALTIME_VAD_TYPE,
  TURN_DETECTOR_MODE,
} from '../const';

const toNum = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

const toInt = (v: unknown): number | undefined => {
  const n = toNum(v);
  return n !== undefined && Number.isInteger(n) ? n : undefined;
};

export const buildTurnDetectorConfig = (
  form: AgentForm
): TurnDetectorConfig | undefined => {
  const {
    turn_detector_mode,
    turn_detector_endpointing_mode,
    turn_detector_endpointing_min_delay,
    turn_detector_endpointing_max_delay,
    turn_detector_interruption_mode,
    turn_detector_interruption_false_interruption_timeout,
    turn_detector_interruption_resume_false_interruption,
    turn_detector_interruption_discard_audio_if_uninterruptible,
    turn_detector_vad_activation_threshold,
    turn_detector_vad_min_speech_duration,
    turn_detector_vad_min_silence_duration,
    turn_detector_vad_prefix_padding_duration,
    turn_detector_vad_max_buffered_speech,
    turn_detector_vad_sample_rate,
    turn_detector_vad_force_cpu,
    turn_detector_model_unlikely_threshold,
    turn_detector_model_model_path,
    turn_detector_realtime_vad_type,
    turn_detector_realtime_vad_eagerness,
    turn_detector_realtime_vad_threshold,
    turn_detector_realtime_vad_prefix_padding_ms,
    turn_detector_realtime_vad_silence_duration_ms,
  } = form;

  const effectiveMode =
    turn_detector_mode ?? TURN_DETECTOR_MODE.MULTILINGUAL_MODEL;

  const showEndpointing = (
    MODES_WITH_ENDPOINTING as readonly string[]
  ).includes(effectiveMode);
  const showInterruption = (
    MODES_WITH_INTERRUPTION as readonly string[]
  ).includes(effectiveMode);
  const showVad = (MODES_WITH_VAD as readonly string[]).includes(effectiveMode);
  const showModel = effectiveMode === TURN_DETECTOR_MODE.MULTILINGUAL_MODEL;
  const showRealtimeVad = effectiveMode === TURN_DETECTOR_MODE.REALTIME_LLM;

  const config: TurnDetectorConfig = { mode: effectiveMode };

  if (showEndpointing) {
    const endpointing: TurnDetectorConfig['endpointing'] = {
      mode: turn_detector_endpointing_mode ?? ENDPOINTING_MODE.FIXED,
    };
    const minDelay = toNum(turn_detector_endpointing_min_delay);
    const maxDelay = toNum(turn_detector_endpointing_max_delay);
    if (minDelay !== undefined) {
      endpointing.min_delay = minDelay;
    }
    if (maxDelay !== undefined) {
      endpointing.max_delay = maxDelay;
    }
    config.endpointing = endpointing;
  }

  if (showInterruption) {
    const interruption: TurnDetectorConfig['interruption'] = {};
    if (turn_detector_interruption_mode !== undefined) {
      interruption.mode = turn_detector_interruption_mode;
    }
    const timeout = toNum(turn_detector_interruption_false_interruption_timeout);
    if (timeout !== undefined) {
      interruption.false_interruption_timeout = timeout;
    }
    if (turn_detector_interruption_resume_false_interruption != null) {
      interruption.resume_false_interruption =
        turn_detector_interruption_resume_false_interruption;
    }
    if (turn_detector_interruption_discard_audio_if_uninterruptible != null) {
      interruption.discard_audio_if_uninterruptible =
        turn_detector_interruption_discard_audio_if_uninterruptible;
    }
    if (Object.keys(interruption).length > 0) {
      config.interruption = interruption;
    }
  }

  if (showVad) {
    const vad: TurnDetectorConfig['vad'] = {};
    const activationThreshold = toNum(turn_detector_vad_activation_threshold);
    const minSpeech = toNum(turn_detector_vad_min_speech_duration);
    const minSilence = toNum(turn_detector_vad_min_silence_duration);
    const prefixPadding = toNum(turn_detector_vad_prefix_padding_duration);
    const maxBuffered = toNum(turn_detector_vad_max_buffered_speech);

    if (activationThreshold !== undefined) {
      vad.activation_threshold = activationThreshold;
    }
    if (minSpeech !== undefined) {
      vad.min_speech_duration = minSpeech;
    }
    if (minSilence !== undefined) {
      vad.min_silence_duration = minSilence;
    }
    if (prefixPadding !== undefined) {
      vad.prefix_padding_duration = prefixPadding;
    }
    if (maxBuffered !== undefined) {
      vad.max_buffered_speech = maxBuffered;
    }
    if (turn_detector_vad_sample_rate != null) {
      vad.sample_rate = turn_detector_vad_sample_rate;
    }
    if (turn_detector_vad_force_cpu != null) {
      vad.force_cpu = turn_detector_vad_force_cpu;
    }
    if (Object.keys(vad).length > 0) {
      config.vad = vad;
    }
  }

  if (showModel) {
    const model: TurnDetectorConfig['model'] = {};
    const unlikelyThreshold = toNum(turn_detector_model_unlikely_threshold);
    if (unlikelyThreshold !== undefined) {
      model.unlikely_threshold = unlikelyThreshold;
    }
    if (
      turn_detector_model_model_path != null &&
      turn_detector_model_model_path !== ''
    ) {
      model.model_path = turn_detector_model_model_path;
    }
    if (Object.keys(model).length > 0) {
      config.model = model;
    }
  }

  if (showRealtimeVad) {
    const realtimeVad: TurnDetectorConfig['realtime_vad'] = {
      vad_type:
        turn_detector_realtime_vad_type ?? REALTIME_VAD_TYPE.SEMANTIC_VAD,
    };
    if (turn_detector_realtime_vad_type === REALTIME_VAD_TYPE.SEMANTIC_VAD) {
      if (turn_detector_realtime_vad_eagerness != null) {
        realtimeVad.eagerness = turn_detector_realtime_vad_eagerness;
      }
    } else {
      const threshold = toNum(turn_detector_realtime_vad_threshold);
      const prefixMs = toInt(turn_detector_realtime_vad_prefix_padding_ms);
      const silenceMs = toInt(turn_detector_realtime_vad_silence_duration_ms);

      if (threshold !== undefined) {
        realtimeVad.threshold = threshold;
      }
      if (prefixMs !== undefined) {
        realtimeVad.prefix_padding_ms = prefixMs;
      }
      if (silenceMs !== undefined) {
        realtimeVad.silence_duration_ms = silenceMs;
      }
    }
    config.realtime_vad = realtimeVad;
  }

  return config;
};
